import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, Check, Instagram, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export function Onboarding() {
  const { t } = useT();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [niche, setNiche] = useState(null);
  const [loading, setLoading] = useState(false);

  const typeKeys = ["creator", "influencer", "personal_brand", "business", "smm", "agency", "other"];
  const nicheKeys = ["travel", "fitness", "beauty", "fashion", "food", "business", "finance", "entertainment", "lifestyle", "gaming", "education", "technology", "adult", "other"];

  const finish = async () => {
    setLoading(true);
    try {
      await api.put("/auth/onboarding", { user_type: type, niche });
      await api.post("/instagram/connect");
      await refresh();
      toast.success("Instagram collegato!");
      navigate("/dashboard");
    } catch {
      toast.error(t("errors.generic"));
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen gp-grid-bg">
      <div className="absolute top-5 left-5"><Logo /></div>
      <div className="absolute top-5 right-5"><ThemeToggle /></div>
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center px-4 py-24">
        <Progress value={(step / 4) * 100} className="mb-8 h-1.5" />
        <div className="mb-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("onboarding.step")} {step}/4</div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            {step === 1 && (
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 gp-float">
                  <Rocket className="h-8 w-8 text-white" />
                </div>
                <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("onboarding.welcomeTitle")}</h1>
                <p className="mx-auto mt-3 max-w-md text-muted-foreground">{t("onboarding.welcomeDesc")}</p>
                <Button onClick={() => setStep(2)} data-testid="onboarding-start" className="mt-8 h-11 rounded-xl bg-brand-500 hover:bg-brand-600 text-black px-8">
                  {t("common.continue")} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            )}
            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl font-bold sm:text-3xl">{t("onboarding.typeTitle")}</h2>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {typeKeys.map((k) => (
                    <button key={k} onClick={() => setType(k)} data-testid={`type-${k}`}
                      className={`rounded-xl border p-4 text-left text-sm font-medium transition-all ${type === k ? "border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-400" : "border-border hover:border-brand-500/40"}`}>
                      {t(`onboarding.types.${k}`)}
                    </button>
                  ))}
                </div>
                <div className="mt-8 flex gap-3">
                  <Button variant="ghost" onClick={() => setStep(1)}>{t("common.back")}</Button>
                  <Button onClick={() => setStep(3)} disabled={!type} data-testid="type-next" className="rounded-xl bg-brand-500 hover:bg-brand-600 text-black px-8">{t("common.next")}</Button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div>
                <h2 className="font-display text-2xl font-bold sm:text-3xl">{t("onboarding.nicheTitle")}</h2>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {nicheKeys.map((k) => (
                    <button key={k} onClick={() => setNiche(k)} data-testid={`niche-${k}`}
                      className={`rounded-xl border p-3.5 text-left text-sm font-medium transition-all ${niche === k ? "border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-400" : "border-border hover:border-brand-500/40"}`}>
                      {t(`onboarding.niches.${k}`)}
                    </button>
                  ))}
                </div>
                <div className="mt-8 flex gap-3">
                  <Button variant="ghost" onClick={() => setStep(2)}>{t("common.back")}</Button>
                  <Button onClick={() => setStep(4)} disabled={!niche} data-testid="niche-next" className="rounded-xl bg-brand-500 hover:bg-brand-600 text-black px-8">{t("common.next")}</Button>
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-orange-400">
                  <Instagram className="h-8 w-8 text-white" />
                </div>
                <h2 className="font-display text-2xl font-bold sm:text-3xl">{t("onboarding.connectTitle")}</h2>
                <p className="mx-auto mt-3 max-w-md text-muted-foreground">{t("onboarding.connectDesc")}</p>
                <Button onClick={finish} disabled={loading} data-testid="connect-instagram-btn" className="mt-8 h-11 rounded-xl bg-brand-500 hover:bg-brand-600 text-black px-8">
                  {loading ? t("common.loading") : (<><Check className="mr-1.5 h-4 w-4" /> {t("onboarding.connectBtn")}</>)}
                </Button>
                <div className="mt-4"><Button variant="ghost" onClick={() => setStep(3)}>{t("common.back")}</Button></div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
